<script>
  import { createEventDispatcher } from 'svelte'
  
  const PREVIOUS_PAGE = 'PREVIOUS_PAGE'
  const NEXT_PAGE = 'NEXT_PAGE'
  const ELLIPSIS = 'ELLIPSIS'
  const dispatch = createEventDispatcher()
  
  export let totalItems = 0
  export let pageSize = 1
  export let currentPage = 1
  export let limit = null
  export let showStepOptions = false
  
  $: options = generateNavigationOptions({
    totalItems,
    pageSize,
    currentPage,
    limit,
    showStepOptions
  })
  
  $: totalPages = Math.ceil(totalItems / pageSize)
  
  function handleOptionClick (option) {
    dispatch('setPage', { page: option.value })
  }
  
  function generateNavigationOptions({ totalItems, pageSize, currentPage, limit = null, showStepOptions = false })  {
    const totalPages = Math.ceil(totalItems / pageSize)
    const limitThreshold = getLimitThreshold({ limit })
    const limited = limit && totalPages > limitThreshold
    let options = limited
      ? generateLimitedOptions({ totalPages, limit, currentPage })
      : generateUnlimitedOptions({ totalPages })
    return showStepOptions
      ? addStepOptions({ options, currentPage, totalPages })
      : options
  }
  
  function generateUnlimitedOptions ({ totalPages }) {
    return new Array(totalPages)
      .fill(null)
      .map((value, index) => ({
        type: 'number',
        value: index + 1
      }))
  }
  
  function generateLimitedOptions ({ totalPages, limit, currentPage }) {
    const boundarySize = limit * 2 + 2
    const firstBoundary = 1 + boundarySize
    const lastBoundary = totalPages - boundarySize
    const totalShownPages = firstBoundary + 2
  
    if (currentPage <= firstBoundary - limit) {
      return Array(totalShownPages)
        .fill(null)
        .map((value, index) => {
          if (index === totalShownPages - 1) {
            return {
              type: 'number',
              value: totalPages
            }
          } else if (index === totalShownPages - 2) {
            return {
              type: 'symbol',
              symbol: ELLIPSIS,
              value: firstBoundary + 1
            }
          }
          return {
            type: 'number',
            value: index + 1
          }
        })
    } else if (currentPage >= lastBoundary + limit) {
      return Array(totalShownPages)
        .fill(null)
        .map((value, index) => {
          if (index === 0) {
            return {
              type: 'number',
              value: 1
            }
          } else if (index === 1) {
            return {
              type: 'symbol',
              symbol: ELLIPSIS,
              value: lastBoundary - 1
            }
          }
          return {
            type: 'number',
            value: lastBoundary + index - 2
          }
        })
    } else if (currentPage >= (firstBoundary - limit) && currentPage <= (lastBoundary + limit)) {
      return Array(totalShownPages)
        .fill(null)
        .map((value, index) => {
          if (index === 0) {
            return {
              type: 'number',
              value: 1
            }
          } else if (index === 1) {
            return {
              type: 'symbol',
              symbol: ELLIPSIS,
              value: currentPage - limit + (index - 2)
            }
          } else if (index === totalShownPages - 1) {
            return {
              type: 'number',
              value: totalPages
            }
          } else if (index === totalShownPages - 2) {
            return {
              type: 'symbol',
              symbol: ELLIPSIS,
              value: currentPage + limit + 1
            }
          }
          return {
            type: 'number',
            value: currentPage - limit + (index - 2)
          }
        })
    }
  }
  
  function addStepOptions ({ options, currentPage, totalPages }) {
    return [
      {
        type: 'symbol',
        symbol: PREVIOUS_PAGE,
        value: currentPage <= 1 ? 1 : currentPage - 1
      },
      ...options,
      {
        type: 'symbol',
        symbol: NEXT_PAGE,
        value: currentPage >= totalPages ? totalPages : currentPage + 1
      }
    ]
  }
  
  function getLimitThreshold ({ limit }) {
    const maximumUnlimitedPages = 3 // This means we cannot limit 3 pages or less
    const numberOfBoundaryPages = 2 // The first and last pages are always shown
    return limit * 2 + maximumUnlimitedPages + numberOfBoundaryPages
  }
</script>
<div class="pagination justify-content-center">
  {#each options as option}
    <li class="page-item">
    <a
      href="?page=3"
      class="option page-link"
      class:number="{option.type === 'number'}"
      class:prev="{option.type === 'symbol' && option.symbol === PREVIOUS_PAGE}"
      class:next="{option.type === 'symbol' && option.symbol === NEXT_PAGE}"
      class:disabled="{
        (option.type === 'symbol' && option.symbol === NEXT_PAGE && currentPage >= totalPages) ||
        (option.type === 'symbol' && option.symbol === PREVIOUS_PAGE && currentPage <= 1)
      }"
      class:ellipsis="{option.type === 'symbol' && option.symbol === ELLIPSIS}"
      class:active="{option.type === 'number' && option.value === currentPage}"
      on:click|preventDefault="{() => handleOptionClick(option)}"
    >
      {#if option.type === 'number'}
        <slot name="number" value="{option.value}">
          <span>{option.value}</span>
        </slot>
      {:else if option.type === 'symbol' && option.symbol === ELLIPSIS}
        <slot name="ellipsis">
          <span>...</span>
        </slot>
      {:else if option.type === 'symbol' && option.symbol === PREVIOUS_PAGE}
        <slot name="prev">
          <svg
            style="width:1em;height:1em"
            viewBox="0 0 24 24"
          >
            <path
              fill="#000000"
              d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"
            />
          </svg>
        </slot>
      {:else if option.type === 'symbol' && option.symbol === NEXT_PAGE}
        <slot name="next">
          <svg
            style="width:1em;height:1em"
            viewBox="0 0 24 24"
          >
            <path
              fill="#000000"
              d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"
            />
          </svg>
        </slot>
      {/if}
    </a>
    </li>
  {/each}
</div>
